import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, WalletCards, ArrowDownRight, ArrowUpRight } from "lucide-react";
import client, { extractError } from "@/api";

const TYPE_LABELS = {
  INITIAL_CREDIT: "Initial credit",
  PURCHASE_DEBIT: "Purchase",
  SALE_CREDIT: "Sale",
  ADMIN_ADJUSTMENT: "Admin adjustment",
  REVERSAL: "Reversal",
  REFUND: "Refund",
  EXPIRY: "Expiry",
  SUSPENSION_HOLD: "Suspension hold",
};

const TYPE_OPTIONS = [
  { value: "", label: "All entries" },
  ...Object.entries(TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })),
];

export default function CreditsPage() {
  const [account, setAccount] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [entryType, setEntryType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAccount = async () => {
    const res = await client.get("/credits/account");
    setAccount(res.data);
  };

  const loadLedger = async (targetPage = 1) => {
    const params = new URLSearchParams();
    if (entryType) params.set("entry_type", entryType);
    params.set("page", targetPage); params.set("page_size", 25);
    const res = await client.get(`/credits/ledger?${params.toString()}`);
    setItems(res.data.items || []); setPage(res.data.page); setTotalPages(res.data.total_pages || 1); setTotal(res.data.total || 0);
  };

  useEffect(() => {
    (async () => {
      setLoading(true); setError("");
      try { await Promise.all([loadAccount(), loadLedger(1)]); }
      catch (err) { setError(extractError(err, "Unable to load credit workspace.")); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => { if (!loading) loadLedger(1).catch((err) => setError(extractError(err))); /* eslint-disable-next-line */ }, [entryType]);

  const util = useMemo(() => account?.utilization_percent || 0, [account]);

  if (loading) return <div className="loading-block">Loading credits…</div>;

  if (!account || account.status === "PENDING") {
    return (
      <div className="empty-state" data-testid="credits-empty">
        <WalletCards size={32} />
        <p><strong>No trade credit yet</strong></p>
        <p className="muted small">Your credit account activates after Barter India verifies your company and assigns a subscription plan.</p>
      </div>
    );
  }

  return (
    <div className="credits-page" data-testid="credits-page">
      <section className="credit-hero" data-testid="credit-hero-card">
        <div className="credit-hero-head">
          <div>
            <p className="eyebrow">BARTER CREDIT</p>
            <h2>₹{account.available_credit.toLocaleString("en-IN")}</h2>
            <p className="muted">Available to spend across the marketplace</p>
          </div>
          <div className="credit-plan" data-testid="credit-plan-chip">
            {account.subscription ? (
              <>
                <p className="eyebrow">CURRENT PLAN</p>
                <strong>{account.subscription.plan_name}</strong>
                <small className="muted">Valid till {new Date(account.subscription.end_date).toLocaleDateString()}</small>
              </>
            ) : (
              <>
                <p className="eyebrow">CURRENT PLAN</p>
                <strong>Not assigned</strong>
              </>
            )}
          </div>
        </div>
        <div className="credit-stats-row">
          <div><p className="eyebrow">CREDIT LIMIT</p><strong data-testid="credit-limit">₹{account.current_credit_limit.toLocaleString("en-IN")}</strong></div>
          <div><p className="eyebrow">USED CREDIT</p><strong data-testid="credit-used">₹{account.used_credit.toLocaleString("en-IN")}</strong></div>
          <div><p className="eyebrow">UTILIZATION</p><strong data-testid="credit-utilization">{util}%</strong></div>
          <div><p className="eyebrow">LIFETIME RECEIVED</p><strong>₹{account.lifetime_received.toLocaleString("en-IN")}</strong></div>
        </div>
        <div className="progress-bar" role="progressbar" aria-valuenow={util} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${Math.min(100, util)}%` }} /></div>
      </section>

      <section className="documents-section">
        <div className="section-heading"><div><p className="eyebrow">CREDIT HISTORY</p><h3>Ledger</h3></div>
          <select value={entryType} onChange={(e) => setEntryType(e.target.value)} data-testid="credits-type-filter">
            {TYPE_OPTIONS.map((t) => <option key={t.value || "all"} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        {error && <div className="form-error">{error}</div>}
        <ul className="ledger-list" data-testid="credits-ledger-list">
          {items.length === 0 && <li className="muted small">No credit activity yet.</li>}
          {items.map((e) => (
            <li key={e.id} data-testid={`credit-entry-${e.id}`}>
              <div className={`icon ${e.credit_amount > 0 ? "credit" : "debit"}`}>
                {e.credit_amount > 0 ? <ArrowDownRight size={14} /> : <ArrowUpRight size={14} />}
              </div>
              <div className="entry-body">
                <div className="entry-head">
                  <strong>{TYPE_LABELS[e.entry_type] || e.entry_type}</strong>
                  {e.related_company_name && <small className="muted"> · {e.related_company_name}</small>}
                </div>
                <p>{e.description}</p>
                <small className="muted">Ref {e.reference_id} · {new Date(e.created_at).toLocaleString()}</small>
              </div>
              <div className="entry-amount">
                {e.credit_amount > 0 && <span className="credit">+₹{e.credit_amount.toLocaleString("en-IN")}</span>}
                {e.debit_amount > 0 && <span className="debit">−₹{e.debit_amount.toLocaleString("en-IN")}</span>}
                <small className="muted">Bal ₹{e.balance_after.toLocaleString("en-IN")}</small>
              </div>
            </li>
          ))}
        </ul>
        <div className="pagination">
          <button type="button" className="ghost-button" data-testid="credits-prev" onClick={() => loadLedger(page - 1)} disabled={page <= 1}><ChevronLeft size={14} /> Previous</button>
          <span>Page {page} of {totalPages} · {total} entries</span>
          <button type="button" className="ghost-button" data-testid="credits-next" onClick={() => loadLedger(page + 1)} disabled={page >= totalPages}>Next <ChevronRight size={14} /></button>
        </div>
      </section>
    </div>
  );
}
